import { useState, useEffect, useRef } from "react";
import { apiCall } from "../../services/api-service.js";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { Container, Row, Col, Button, Spinner } from "react-bootstrap";
import upload from "../../assets/svg/upload.svg";
import download from "../../assets/svg/download.svg";
import ModalCloseIcon from "../../assets/svg/close-modal.svg";
import attachment from "../../assets/png/attachment.png";
import closeAttachment from "../../assets/svg/close-red.svg";
import DatePicker from "react-datepicker";
import { Calendar } from "lucide-react";
import CommonDropdown from "../common/common_dropDown";
import ProfileImageModal from "../common/common-preview.jsx";

const PaymentUpdateModal = ({ id, onHide, success }) => {
  const [showProfilePopup, setShowProfilePopup] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState("");
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");
  const modalStyle = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      opacity: 1,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000,
    },
    modal: {
      backgroundColor: "#ffffff",
      padding: "16px 24px",
      borderRadius: "16px",
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
      width: "40%",
      height: "fit-content",
      overflowY: "auto",
    },
  };

  const [paymentDate, setPaymentDate] = useState();
  const [payingAmount, setPayingAmount] = useState("");
  const [paymentTypeOption, setPaymentTypeOption] = useState(null);

  const [errorMsg, setErrorMsg] = useState({
    date: "",
    amount: "",
    payment_type_id: "",
    remarks: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [descriptionInputValue, setDescriptionInputValue] = useState("");
  const [lastKey, setLastKey] = useState("");
  const [showPicker, setShowPicker] = useState(false);
  const [selectedEmailOption, setEmailOption] = useState(null);
  const [selectedTeamOption, setTeamOption] = useState(null);
  const [remarkType, setRemarksType] = useState("single");

  const [selectedTicketList, setSelectedTicketList] = useState([]);
  const [groupedError, setGroupedError] = useState({});

  const fileInputRef = useRef(null);
  const [image, setImage] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [attachmentURLList, setAttachmentURLList] = useState([]);
  const [attachmentList, setAttachmentList] = useState([]);
  const [removedAttachmentList, setRemovedAttachmentList] = useState([]);

  // Modify state variables for single attachment
  const [attachment, setAttachment] = useState(null);
  const [removedAttachment, setRemovedAttachment] = useState(null);

  useEffect(() => {
    // Attach event listener to the document
    document.addEventListener("mousedown", handleCloseDatePicker);
    return () => {
      // Clean up event listener on unmount
      document.removeEventListener("mousedown", handleCloseDatePicker);
    };
  }, []);
  const alertDatePicRef = useRef(null);
  const [alertIndex, setAlertIndex] = useState(null);
  const handleCloseDatePicker = (event) => {
    if (alertDatePicRef.current && !alertDatePicRef.current.contains(event.target)) {
      setShowPicker(false);
    }
  };

  const openFile = (fileUrl = "", fileName = "") => {
    // prefer filename extension (CREATE flow)
    const source = (fileName || fileUrl).toLowerCase();
    const cleanSource = source.split("?")[0];

    // 🖼 Images → popup
    if (
      cleanSource.endsWith(".png") ||
      cleanSource.endsWith(".jpg") ||
      cleanSource.endsWith(".jpeg") ||
      cleanSource.endsWith(".gif") ||
      cleanSource.endsWith(".webp")
    ) {
      if (!fileUrl) return; // no preview without URL
      setSelectedProfile(fileUrl);
      setShowProfilePopup(true);
      return;
    }

    // 📄 PDF → open directly
    if (cleanSource.endsWith(".pdf")) {
      if (!fileUrl) return;
      setSelectedProfile(fileUrl);
      setShowProfilePopup(true);
      return;
    }

    // 📊 Office files → Office Viewer
    if (
      cleanSource.endsWith(".xls") ||
      cleanSource.endsWith(".xlsx") ||
      cleanSource.endsWith(".doc") ||
      cleanSource.endsWith(".docx") ||
      cleanSource.endsWith(".ppt") ||
      cleanSource.endsWith(".pptx")
    ) {
      if (!fileUrl) return;

      const officeViewerUrl =
        "https://view.officeapps.live.com/op/view.aspx?src=" +
        encodeURIComponent(fileUrl);

      window.open(officeViewerUrl, "_blank", "noopener,noreferrer");
      return;
    }

    // ⬇ Fallback
    if (fileUrl) {
      window.open(fileUrl, "_blank", "noopener,noreferrer");
    }
  };

  const paymentUpdateApi = async () => {
    if (loading) return;

    const data = {
      date: paymentDate != null && paymentDate !== "" ? paymentDate.toLocaleDateString("en-CA") : "",
      amount: payingAmount,
      payment_type_id: paymentTypeOption != null ? paymentTypeOption.value : "",
      remarks: descriptionInputValue,
    };

    setLoading(true);

    try {
      const response = await apiCall("put", `transaction/payment_update/${id}`, data);

      if (response.status === 200 && response.data.success === true) {
        success();
        saveAttachment(response.data.id);
      } else {
        const validationTemp = {
          date: "",
          amount: "",
          payment_type_id: "",
          remarks: "",
          message: "",
        };

        const backendErrors = response.data.errors || response.data.error || {};
        Object.entries(backendErrors).forEach(([key, value]) => {
          validationTemp[key] = Array.isArray(value) ? (value[0] || "") : value;
        });
        validationTemp.message = response.data.message ?? "";
        setErrorMsg(validationTemp);
      }
    } catch (error) {
      if (error.response) {
        const validationTemp = {
          date: "",
          amount: "",
          payment_type_id: "",
          remarks: "",
          message: "",
        };

        const backendErrors = error.response.data.errors || error.response.data.error || {};
        Object.entries(backendErrors).forEach(([key, value]) => {
          validationTemp[key] = Array.isArray(value) ? (value[0] || "") : value;
        });
        validationTemp.message = error.response.data.message ?? "";
        setErrorMsg(validationTemp);
      } else {
        console.error("Request failed:", error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Add Attachmnets
  const handleInputFile = () => {
    fileInputRef.current.click();
  };

   const validateUploadedFile = (file) => {
    const allowedExtensions = ["png", "jpg", "jpeg", "pdf", "doc", "docx", "xls", "xlsx", "csv"];
    const fileExtension = file.name.split(".").pop().toLowerCase();
    
    if (!allowedExtensions.includes(fileExtension)) {
      setErrorMsg(prev => ({
        ...prev,
        attachment: `Unsupported file format: ${file.name}! Supported formats: JPG, JPEG, PNG, PDF, DOC, DOCX, XLS, XLSX, CSV`
      }));
      return false;
    }

    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > 5) {
      setErrorMsg(prev => ({
        ...prev,
        attachment: `File size of ${file.name} exceeds 5MB! Maximum file size supported is 5MB.`
      }));
      return false;
    }

    setErrorMsg(prev => ({ ...prev, attachment: "" }));
    return true;
  };

  // Update file handling functions
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!validateUploadedFile(file)) {
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      const reader = new FileReader();
      reader.onload = async () => {
        const base64String = await reader.result;
        setAttachment({
          name: file.name,
          base64: base64String,
          size: formatBytes(file.size),
        });
      };
      reader.readAsDataURL(file);
    }
  };
  const formatBytes = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };
  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);

    const file = event.dataTransfer.files[0];
    if (file) {
      if (!validateUploadedFile(file)) {
        return;
      }
      const reader = new FileReader();
      reader.onload = async () => {
        const base64String = await reader.result;
        setAttachment({
          name: file.name,
          base64: base64String,
          size: formatBytes(file.size),
        });
      };
      reader.readAsDataURL(file);
    }
  };
  const handleDelete = () => {
    setImage(null);
    fileInputRef.current.value = "";
  };
  const downloadFile = (fileUrl, fileName) => {
    fetch(fileUrl)
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => console.error("Download error:", error));
  };

  const saveAttachment = async (parentId) => {
    try {
      if (!attachment || !attachment.base64) {
        return;
      }

      const data = {
        attachment: attachment.base64,
        file_name: attachment.name,
      };

      const response = await apiCall("put", `payment/image/${parentId}`, data);

      if (response.data.success === true) {
        setAttachment(null);
      }
    } catch (err) {
      console.error("Error saving attachment:", err);
      if (err.response) {
        console.error("Error response:", err.response.data);
      }
    }
  };

  return (
    <div style={modalStyle.overlay}>
      <div style={modalStyle.modal}>
        <div style={{ color: "#ffffff" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "start",
              }}
            >
              <div
                style={{
                  paddingLeft: "4px",
                  fontSize: "20px",
                  fontWeight: "500",
                  color: "#212129",
                }}
              >
                New Payment
              </div>
            </div>
            <div className="style-justify-end">
              <div
                onClick={onHide}
                className="style-justify-center cursor-pointer"
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "100px",
                  border: "1px solid #EBEBEB",
                }}
              >
                <img src={ModalCloseIcon} alt="close" />
              </div>
              {/*  <ModalCloseIcon
                style={{ cursor: "pointer" }}
                onClick={onHide}
              /> */}
            </div>
          </div>

          <div style={{ marginTop: "8px" }}>
            <Row style={{ display: "flex", flexWrap: "wrap" }}>
              <Col sm={6} style={{ marginTop: "8px" }}>
                <div
                  className="fs-13 fw-500 mb-1"
                  style={{
                    color: "#212129",
                  }}
                >
                  Date<span style={{ color: "red" }}> *</span>
                </div>
                <DatePicker
                  selected={paymentDate}
                  onChange={(date) => setPaymentDate(date)}
                  dateFormat="yyyy-MM-dd"
                  maxDate={new Date()}
                  customInput={
                    <button style={{ color: "#8790A1", width: "100%" }} className="w-100 btn-border-grey form-control vehicle-form-input bg-white rounded-3 px-3 py-2 d-flex justify-content-between align-items-center gap-2">
                      {paymentDate ? paymentDate.toLocaleDateString("en-GB").replace(/\//g, "-") : <span style={{ color: "#8790A1" }}>dd-mm-yyyy</span>}
                      <Calendar size={16} />
                    </button>
                  }
                />
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "start",
                    fontSize: "12px",
                    fontWeight: 400,
                    color: "#FF4444",
                    marginTop: "6px",
                    marginLeft: "6px",
                  }}
                >
                  {errorMsg.date}
                </span>
              </Col>
              <Col sm={6} style={{ marginTop: "8px" }}>
                <div
                  className="fs-13 fw-500 mb-1"
                  style={{
                    color: "#212129",
                  }}
                >
                  Payment Type<span style={{ color: "red" }}> *</span>
                </div>

                <div className="w-100">
                  <CommonDropdown
                    apiUrl={`${apiUrl}get_payment_type?term=`}
                    placeholder="Select"
                    value={paymentTypeOption}
                    onSelect={(item) => {
                      setPaymentTypeOption(item);
                    }}
                  />{" "}
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "start",
                      fontSize: "12px",
                      fontWeight: 400,
                      color: "#FF4444",
                      marginTop: "6px",
                      marginLeft: "6px",
                    }}
                  >
                    {errorMsg.payment_type_id}
                  </span>
                </div>
              </Col>
              <Col sm={6} style={{ marginTop: "8px" }}>
                <div
                  className="fs-13 fw-500 mb-1"
                  style={{
                    color: "#212129",
                  }}
                >
                  Amount<span style={{ color: "red" }}> *</span>
                </div>
                <input
                  type="text"
                  className="form-control rounded-3 vendor-form-input"
                  id="PayingAmount"
                  placeholder="Enter here"
                  value={payingAmount}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow only digits and a single decimal point
                    const formatted = value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
                    setPayingAmount(formatted);
                  }}
                />

                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "start",
                    fontSize: "12px",
                    fontWeight: 400,
                    color: "#FF4444",
                    marginTop: "6px",
                    marginLeft: "6px",
                  }}
                >
                  {errorMsg.amount}
                </span>
              </Col>


              <Col sm={6} style={{ marginTop: "8px" }}>
                <div
                  className="fs-13 fw-500 mb-1"
                  style={{
                    color: "#212129",
                  }}
                >
                  Remarks
                </div>

                <textarea
                  type="text"
                  placeholder=" "
                  className="form-control mt-1"
                  value={descriptionInputValue}
                  rows={1}
                  onChange={(event) => {
                    setDescriptionInputValue(event.target.value);
                  }}
                />
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "start",
                    fontSize: "12px",
                    fontWeight: 400,
                    color: "#FF4444",
                  }}
                >
                  {errorMsg.remarks}
                </span>
              </Col>
              {errorMsg.message !== 'Validation error' && (<span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "start",
                  fontSize: "12px",
                  fontWeight: 400,
                  color: "#FF4444",
                  marginTop: "6px",
                  marginLeft: "6px",
                }}
              >
                {errorMsg.message}
              </span>)}

              <Col sm={12} style={{ marginTop: "8px" }}>
                <div className="bg-white rounded-4" style={{ color: "#212129" }}>
                  <div className="row">
                    <div
                      className="fs-13 fw-500 mb-1"
                      style={{
                        color: "#212129",
                      }}
                    >
                      Attachment
                    </div>
                    {!attachment ? (
                      <div
                        className="col-12 p-2 d-flex flex-column rounded-4 align-items-center justify-content-center m-2"
                        style={{
                          border: "1px dashed #9FBCFD",
                          background: "#FAFAFA",
                          maxWidth: "97%",
                        }}
                      >
                        <div onClick={handleInputFile} onDrop={handleDrop} onDragOver={(e) => e.preventDefault()} onDragEnter={() => setDragging(true)} onDragLeave={() => setDragging(false)} className="d-flex flex-column justify-content-center align-items-center gap-3">
                          <img src={upload} alt="Upload Icon" style={{ width: "60px", height: "60px" }} />
                          <p className="fw-semibold m-0 p-0">Upload document</p>
                          <p className="m-0 p-0 text-muted text-center" style={{ fontSize: "11px", color: "#8790A1" }}>Supported formats: JPEG, PNG, PDF (Max 5 MB)</p>
                        </div>

                        <Button className="mt-1 border-0 rounded-pill text-white px-4 py-2 fw-500" style={{ background: "#4F46E5" }} onClick={handleInputFile}>
                          Select a file
                        </Button>

                        <input type="file" accept="image/*,application/pdf" ref={fileInputRef} className="d-none" onChange={handleFileChange} />
                      </div>
                    ) : (
                      <div
                        style={{
                          marginTop: "16px",
                          display: "flex",
                          justifyContent: "start",
                          cursor: 'pointer'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          openFile(attachment.base64, attachment.name);
                        }}
                      >
                        <div
                          style={{
                            width: "312px",
                            height: "62px",
                            borderRadius: "12px",
                            padding: "12px 16px 12px 16px",
                            backgroundColor: "#F7F7F7",
                            display: "flex",
                            alignItems: "center", cursor: 'pointer'
                          }}
                        >
                          {attachment && (<img src={attachment.base64} style={{ width: "37px", height: "36px" }} />)}
                          <div
                            style={{
                              width: "210px",
                              marginLeft: "8px",
                              marginTop: "4px",
                            }}
                          >
                            <div
                              style={{
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                                fontWeight: "500",
                                fontSize: "13px",
                                lineHeight: "17.55px",
                                color: "#212129",
                                marginBottom: "2px",
                              }}
                            >
                              {attachment.name}
                            </div>
                            <p
                              style={{
                                fontWeight: "400",
                                fontSize: "12px",
                                lineHeight: "16.2px",
                                color: "#8790A1",
                              }}
                            >
                              {attachment.size}
                            </p>
                          </div>

                          <div
                            style={{
                              position: "relative",
                              width: "32px",
                              height: "32px",
                              borderRadius: "100px",
                              marginLeft: "4px",
                            }}
                          >
                            <img
                              src={closeAttachment}
                              style={{
                                width: "12.8px",
                                height: "12px",
                                top: "10px",
                                left: "9.6px",
                                cursor: "pointer",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setAttachment(null);
                                if (fileInputRef.current) {
                                  fileInputRef.current.value = "";
                                }
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    {errorMsg.attachment && (
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "start",
                          fontSize: "12px",
                          fontWeight: 400,
                          color: "#FF4444",
                          marginTop: "6px",
                          marginLeft: "6px",
                        }}
                      >
                        {errorMsg.attachment}
                      </span>
                    )}
                  </div>
                </div>
              </Col>

              <Row
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "end",
                  marginTop: "20px",
                }}
              >
                <div
                  onClick={onHide}
                  style={{
                    cursor: "pointer",
                    width: "176px",
                    height: "44px",
                    marginLeft: "8px",
                    marginRight: "8px",
                    borderRadius: "100px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "#FFFFFF",
                    border: "0.6px solid #E5E5E5",
                  }}
                >
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "700",
                      color: "#1E1E1E",
                    }}
                  >
                    Cancel
                  </div>
                </div>
                <div
                  onClick={(e) => {
                    if (loading === false) {
                      paymentUpdateApi();
                    }
                  }}
                  style={{
                    cursor: loading ? "not-allowed" : "pointer",
                    width: "176px",
                    height: "44px",
                    marginLeft: "8px",
                    marginRight: "8px",
                    borderRadius: "100px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "#3253EB",
                    opacity: loading ? 0.7 : 1,
                  }}
                >
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "700",
                      color: "#FFFFFF",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    {loading ? <Spinner animation="border" size="sm" /> : "Save"}
                  </div>
                </div>
              </Row>
            </Row>

            <div></div>
          </div>
        </div>
      </div>
      <ProfileImageModal show={showProfilePopup} onClose={() => setShowProfilePopup(false)} imageUrl={selectedProfile} />
    </div>
  );
};

export default PaymentUpdateModal;
